import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShareButtons } from '../utils'
import { RelatedNews } from '../cards'
import { useRelatedNews } from '../../hooks/useRelatedNews'
import { buildFileUrl } from '../../config/strapi'

interface NoticiaIndividualPageProps {
  noticia: any
  navigationOrigin?: 'home' | 'noticias'
}

// Função para formatar data e calcular tempo decorrido
const formatDateAndTimeAgo = (noticia: any) => {
  // Tentar diferentes caminhos para encontrar o updatedAt
  const updatedAt = noticia?.dataAtualizacao || noticia?.attributes?.dataAtualizacao || noticia?.noticiaPage?.dataAtualizacao
  
  if (!updatedAt) return 'Data não disponível'
  
  const updateDate = new Date(updatedAt)
  const now = new Date()
  const diffInMs = now.getTime() - updateDate.getTime()
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))
  
  // Formatar a data
  const day = updateDate.getDate().toString().padStart(2, '0')
  const month = (updateDate.getMonth() + 1).toString().padStart(2, '0')
  const year = updateDate.getFullYear()
  const hours = updateDate.getHours().toString().padStart(2, '0')
  const minutes = updateDate.getMinutes().toString().padStart(2, '0')
  
  const formattedDate = `${day}/${month}/${year} ${hours}h${minutes}`
  
  // Calcular tempo decorrido
  let timeAgo = ''
  if (diffInDays > 0) {
    timeAgo = `Atualizado há ${diffInDays} ${diffInDays === 1 ? 'dia' : 'dias'}`
  } else if (diffInHours > 0) {
    timeAgo = `Atualizado há ${diffInHours} ${diffInHours === 1 ? 'hora' : 'horas'}`
  } else {
    timeAgo = 'Atualizado há menos de 1 hora'
  }
  
  return `${formattedDate} ${timeAgo}`
}

export function NoticiaIndividualPage({ noticia, navigationOrigin = 'noticias' }: NoticiaIndividualPageProps) {
  const navigate = useNavigate()
  const { noticias: allNoticias } = useRelatedNews(noticia?.id)
  const [isMobile, setIsMobile] = useState(false)
  const [isSmallMobile, setIsSmallMobile] = useState(false)

  // Hook para detectar tamanho da tela
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
      setIsSmallMobile(window.innerWidth <= 480)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Função para processar o texto e separar em parágrafos
  const processText = (text: string) => {
    if (!text) return null
    
    // Separar o texto por quebras de linha duplas (\n\n)
    const paragraphs = text.split('\n\n').filter(paragraph => paragraph.trim() !== '')
    
    return paragraphs.map((paragraph, index) => {
      // Se for o segundo parágrafo (índice 1) e existir imagem secundária, criar layout especial
      if (index === 1 && noticia.noticiaPage?.secImage) {
        return (
          <div key={index} style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isSmallMobile ? '1rem' : isMobile ? '1.5rem' : '2rem',
            marginBottom: '1.5rem',
            fontSize: isSmallMobile ? '1rem' : isMobile ? '1.1rem' : '1.2rem',
            fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            alignItems: 'flex-start'
          }}>
            <div style={{
              flex: 1,
              fontSize: isSmallMobile ? '1rem' : isMobile ? '1.1rem' : '1.2rem',
              fontStyle: 'Regular',
              lineHeight: 1.8,
              color: '#333',
              textAlign: isMobile ? 'left' : 'justify'
            }}>
              {paragraph.trim()}
            </div>
            <div style={{
              flexShrink: 0,
              width: isMobile ? '100%' : '300px',
              height: isSmallMobile ? '200px' : isMobile ? '250px' : '300px',
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              <img 
                src={buildFileUrl(noticia.noticiaPage.secImage.url)}
                alt={noticia.noticiaPage.secImage.alternativeText || 'Imagem secundária da notícia'}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </div>
          </div>
        )
      }
      
      // Para outros parágrafos, retornar normalmente
      return (
        <p 
          key={index}
          style={{
            marginBottom: '1.5rem',
            textAlign: isMobile ? 'left' : 'justify',
            fontSize: isSmallMobile ? '1rem' : isMobile ? '1.1rem' : '1.2rem'
          }}
        >
          {paragraph.trim()}
        </p>
      )
    })
  }

  if (!noticia) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        background: '#f5f5f5',
        color: '#333'
      }}>
        Notícia não encontrada
      </div>
    )
  }

  const handleVoltar = () => {
    // Reason: Navega de volta para a página de origem (home ou notícias)
    // baseado em como o usuário chegou a esta página
    if (navigationOrigin === 'home') {
      navigate('/')
    } else {
      navigate('/noticias')
    }
  }

  // URL atual para compartilhamento
  const currentUrl = window.location.href
  const shareTitle = noticia.header?.title || 'Notícia'
  const shareDescription = noticia.header?.description || ''

  // Cálculo responsivo do padding-top
  const instBarHeight = 46
  const logoSectionHeight = isSmallMobile ? 70 : isMobile ? 80 : 90
  const spacing = isSmallMobile ? 8 : isMobile ? 12 : 14
  const navbarHeight = isSmallMobile ? 50 : isMobile ? 56 : 64
  const totalTopHeight = instBarHeight + logoSectionHeight + spacing + navbarHeight + 20 // +20px extra para conforto

  return (
    <div style={{ 
      minHeight: '100vh',
      background: '#f5f5f5',
      color: '#333',
      padding: isSmallMobile ? '20px' : isMobile ? '40px' : '100px 375px',
      paddingTop: `${totalTopHeight}px`,
      fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      letterSpacing: 0.5
    }}>
      <div style={{
        padding: isSmallMobile ? '10px 20px' : isMobile ? '20px 30px' : '20px 40px',
        maxWidth: isMobile ? '100%' : '800px',
        margin: '0 auto'
      }}>
        
        {/* Botão Voltar */}
        <div style={{
          marginBottom: '2rem',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center'
        }}>
          <button
            onClick={handleVoltar}
            style={{
              background: 'none',
              border: 'none',
              color: '#0072BB',
              fontSize: isSmallMobile ? '1rem' : '1.1rem',
              fontWeight: 800,
              marginLeft: isMobile ? '0' : '-140px',
              cursor: 'pointer',
              padding: '0',
              textDecoration: 'underline',
              fontFamily: 'inherit'
            }}
          >
            ← Voltar
          </button>
        </div>

        {/* Título */}
        <h1 style={{
          fontSize: isSmallMobile ? '1.8rem' : isMobile ? '2.2rem' : '2.5rem',
          color: '#0072BB',
          fontWeight: 1000,
          textAlign: isMobile ? 'center' : 'left',
          marginBottom: '1rem',
          lineHeight: 1.2
        }}>
          {noticia.header?.title}
        </h1>

        {/* Descrição */}
        <p style={{
          fontSize: isSmallMobile ? '1rem' : isMobile ? '1.05rem' : '1.1rem',
          color: '#898989',
          fontWeight: 800,
          textAlign: isMobile ? 'center' : 'left',
          marginBottom: '2rem',
          lineHeight: 1.6,
          margin: '0 auto 2rem auto'
        }}>
          {noticia.header?.description}
        </p>

        {/* Autor e Data */}
          <div style={{
            textAlign: isMobile ? 'center' : 'left',
            marginBottom: '2rem',
            color: '#141414',
            fontSize: isSmallMobile ? '0.8rem' : '0.9rem',
          }}>
            <div>Por {noticia.noticiaPage?.autor || 'Autor não informado'}</div>
            <div>{formatDateAndTimeAgo(noticia)}</div>
          </div>

          {/* Imagem Principal */}
          {noticia.noticiaPage?.mainImage && (
          <div style={{
            marginBottom: '2rem',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <img 
              src={buildFileUrl(noticia.noticiaPage.mainImage.url)}
              alt={noticia.noticiaPage.mainImage.alternativeText || 'Imagem principal da notícia'}
              style={{
                width: '100%',
                height: isSmallMobile ? '250px' : isMobile ? '350px' : '500px',
                objectFit: 'cover'
              }}
            />
          </div>
        )}

        {/* Texto da Notícia */}
        <div style={{
          fontSize: isSmallMobile ? '1rem' : isMobile ? '1.1rem' : '1.2rem',
          lineHeight: 1.8,
          fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          color: '#333',
          marginBottom: '2rem'
        }}>
          {noticia.noticiaPage?.text ? (
            processText(noticia.noticiaPage.text)
          ) : <></>}
        </div>

        {/* Botões de Compartilhamento */}
        <ShareButtons
          url={currentUrl}
          title={shareTitle}
          description={shareDescription}
          style={{
            marginTop: '3rem',
            marginBottom: '3rem'
          }}
        />

        {/* Seção "Veja também" */}
        <RelatedNews
          noticias={allNoticias}
          currentNoticiaId={noticia.id}
        />

        {/* Texto Final */}
        <div style={{
          fontSize: isSmallMobile ? '1rem' : isMobile ? '1.05rem' : '1.1rem',
          lineHeight: 1.8,
          color: '#333'
        }}>
        </div>

      </div>
    </div>
  )
} 